<template>
  <div>
    <div class="row">
      <div class="col-md-12 left">
        <div class="form-group">
          <label for="filterName">{{ $t('filters.name') }}</label>
          <input
            type="text"
            class="form-control"
            :placeholder="$t('filters.name')"
            id="filterName"
            v-model="filterName"
          />
          <p v-if="filterNameError" class="error">
            {{ $t('filters.name_error') }}
          </p>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12 left">
        {{ $t('filters.must_satisfy') }}<br />
        <button
          class="btn btn-default"
          :class="{active: conditionType == 'AND'}"
          @click="andCondition"
        >
          {{ $t('filters.all_conditions') }}
        </button>
        <button
          class="btn btn-default"
          :class="{active: conditionType == 'OR'}"
          @click="orCondition"
        >
          {{ $t('filters.one_of_conditions') }}
        </button>
        <p v-if="conditionTypeError" class="error">
          {{ $t('filters.type_error') }}
        </p>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <hr />
      </div>
    </div>
    <div class="row">
      <div class="col-md-12 left">
        <table class="table">
          <thead>
            <tr>
              <th>{{ $t('filters.field') }}</th>
              <th>{{ $t('filters.operator') }}</th>
              <th>{{ $t('filters.value') }}</th>
            </tr>
          </thead>
          <display-condition-row
            v-for="(condition, index) in conditions"
            :key="condition.key"
            :last="index === conditions.length - 1"
            @add-condition="addCondition"
            @remove-condition="removeCondition"
            :condition="condition"
            :index="index"
          />
        </table>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <hr />
      </div>
    </div>
    <div class="row">
      <div class="col-md-4">
        <a @click="backToList"
          ><span class="glyphicon glyphicon-arrow-left"></span>
          {{ $t('filters.back') }}</a
        >
      </div>
      <div class="col-md-4 col-md-offset-4">
        <button
          id="applyWithoutSavingBtn"
          class="btn btn-default"
          @click="applyConditions"
        >
          {{ $t('filters.apply') }}
        </button>
      </div>
    </div>
    <br />
    <div class="row">
      <div class="col-md-4 col-mdtnffset-8">
        <button
          id="saveBtn"
          class="btn btn-primary"
          @click="persistCondition(false)"
        >
          {{ $t('filters.save') }}
        </button>
      </div>
      <div v-if="conditionToEdit" class="col-md-4 col-md-offset-4">
        <button
          id="saveAsNewBtn"
          class="btn btn-primary"
          @click="persistCondition(true)"
        >
          {{ $t('filters.save_as_new') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {v4 as uuidv4} from 'uuid';
import DisplayConditionRow from './DisplayConditionRow';
import {mapMutations, mapActions, mapState} from 'vuex';

const checkConditions = (filterName, conditionType) => {
  let filterNameError = false;
  let conditionTypeError = false;

  let valid = true;
  if (filterName === undefined || filterName === '') {
    valid = false;
    filterNameError = true;
  }

  if (conditionType === '') {
    valid = false;
    conditionTypeError = true;
  }

  return {valid, filterNameError, conditionTypeError};
};

const buildPayload = (templateData, filterName, conditionType, conditions) => {
  const payload = {
    mergeTagsSource: templateData.mergeTagsSource.id,
    condition: {
      name: filterName,
      body: {
        conditionType,
        conditions: conditions.map(c => {
          const result = {};
          result.field = c.field;
          result.operator = c.operator;
          result.values = c.value === undefined ? c.values : [c.value];
          return result;
        })
      }
    }
  };

  return payload;
};

export default {
  name: 'DisplayConditionForm',
  components: {
    DisplayConditionRow
  },
  computed: mapState({
    conditionToEdit: state => state.displayModule.conditionToEdit,
    templateData: state => state.templateData,
    error: state => state.error
  }),
  created: function () {
    if (this.conditionToEdit) {
      this.filterName = this.conditionToEdit.name;
      this.conditionType = this.conditionToEdit.body.conditionType;
      this.conditions = this.conditionToEdit.body.conditions.map(c => {
        const key = uuidv4();
        c.key = key;
        return c;
      });
    }
  },
  methods: {
    ...mapMutations('displayModule', [
      'setEditConditions',
      'setConditionToEdit'
    ]),
    ...mapMutations(['setDisplayConditionSaved']),
    ...mapActions(['resolveDisplayCondition']),
    ...mapActions('displayModule', [
      'saveDisplayCondition',
      'updateDisplayCondition'
    ]),
    andCondition() {
      this.conditionType = 'AND';
    },
    orCondition() {
      this.conditionType = 'OR';
    },
    addCondition() {
      const key = uuidv4();
      if (this.conditions.length < 10) {
        this.conditions.push({key});
      }
    },
    applyConditions() {
      const checkResult = checkConditions(this.filterName, this.conditionType);
      this.filterNameError = checkResult.filterNameError;
      this.conditionTypeError = checkResult.conditionTypeError;

      if (checkResult.valid) {
        const payload = buildPayload(
          this.templateData,
          this.filterName,
          this.conditionType,
          this.conditions
        );
        this.resolveDisplayCondition(payload).then(
          function () {
            this.setDisplayConditionSaved(true);
            this.backToList();
            this.$emit('condition-applied');
          }.bind(this)
        );
      }
    },
    persistCondition(saveAsNew) {
      const checkResult = checkConditions(this.filterName, this.conditionType);
      this.filterNameError = checkResult.filterNameError;
      this.conditionTypeError = checkResult.conditionTypeError;

      if (checkResult.valid) {
        const payload = buildPayload(
          this.templateData,
          this.filterName,
          this.conditionType,
          this.conditions
        );
        if (saveAsNew || !this.conditionToEdit) {
          this.saveDisplayCondition(payload).then(
            function () {
              if (this.error === undefined) {
                this.backToList();
              }
            }.bind(this)
          );
        } else {
          payload.conditionId = this.conditionToEdit.id;
          this.updateDisplayCondition(payload).then(
            function () {
              if (this.error === undefined) {
                this.backToList();
              }
            }.bind(this)
          );
        }
      }
    },
    removeCondition(index) {
      if (this.conditions.length > 1) {
        this.conditions.splice(index, 1);
      }
    },
    backToList() {
      this.resetCondition();
      this.setEditConditions(false);
    },
    resetCondition() {
      this.setConditionToEdit(undefined);
      this.filterName = undefined;
      this.conditionType = undefined;
      this.conditions = undefined;
    }
  },
  data: function () {
    const key = uuidv4();
    return {
      conditionType: '',
      conditions: [{key}],
      filterName: '',
      filterNameError: false,
      conditionTypeError: false
    };
  }
};
</script>

<style scoped>
.left {
  text-align: left;
}

hr {
  border-top: 1px dashed;
}

.error {
  color: red;
}
</style>
